import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Datatable from "../../../core/common/dataTable";
import Api from "../../../core/data/redux/utils/apiCall";
import { toast } from "react-toastify";
import { API_URLS } from "../../../core/data/redux/apis/urls";

const years = ['2027', '2026', '2025', '2024', '2023', '2022', '2021', '2020'];

const parseDotNetDate = (dateStr?: string) => {
  if (!dateStr) return "-";
  const match = dateStr.match(/\/Date\((\d+)[+-]?\d*\)\//);
  if (match) {
    const ts = parseInt(match[1], 10);
    return new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.');
  }
  if (dateStr.includes('T')) {
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  }
  return dateStr;
};

const extractDataArray = (res: any): any[] => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.Response?.data)) return res.Response.data;
  if (Array.isArray(res?.data?.data?.data)) return res.data.data.data;
  return [];
};

const APARTeamStatus = () => {
  const routes = all_routes;
  const navigate = useNavigate();

  const [aparList, setAparList] = useState<any[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedYear, setSelectedYear] = useState('2025');
  const [isLoading, setIsLoading] = useState(false);

  const fetchAppraisalStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: any = await Api.post(API_URLS.APAR.APPRAISAL_STATUS, {
        fiscalYear: selectedYear,
      });

      console.log("APAR Team Status response raw:", response);
      const items = extractDataArray(response);
      console.log("APAR Team Status extracted items:", items);

      // Ensure each item has a unique key for Table
      const itemsWithKey = items.map((item: any, idx: number) => ({
        ...item,
        key: item.AppraisalID || item.id || `apar-${idx}`,
      }));

      setAparList(itemsWithKey);
    } catch (err: any) {
      toast.error(err?.message || "Error fetching appraisal status.");
      setAparList([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchAppraisalStatus();
  }, [fetchAppraisalStatus]);

  const filteredData = useMemo(() => {
    let data = aparList;
    if (selectedStatus) {
      const normStatus = selectedStatus.toLowerCase();
      data = data.filter(item => {
        const itemStatus = (item.APARStatus || item.status || '').toString().toLowerCase();
        if (normStatus === 'approved') {
          return itemStatus.includes('approved') || itemStatus.includes('completed');
        }
        if (normStatus === 'pending') {
          return itemStatus.includes('pending') || itemStatus.includes('process') || itemStatus.includes('planning');
        }
        if (normStatus === 'rejected') {
          return itemStatus.includes('rejected');
        }
        return itemStatus.includes(normStatus);
      });
    }
    return data;
  }, [aparList, selectedStatus]);

  const actionBodyTemplate = (rowData: any) => {
    return (
      <div className="d-flex align-items-center gap-2">
        <button 
          className="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
          onClick={() => navigate(routes.apar.details, { state: { item: rowData } })}
          title="View Details"
        >
          <i className="ti ti-eye me-1"></i> Details
        </button>
        <button 
          className="btn btn-outline-info btn-sm d-inline-flex align-items-center"
          onClick={() => navigate(routes.apar.channel, { state: { item: rowData } })}
          title="Employee Channel"
        >
          <i className="ti ti-git-merge me-1"></i> Channel
        </button>
      </div>
    );
  };

  const statusBodyTemplate = (rowData: any) => {
    const statusStr = (rowData.APARStatus || rowData.status || 'Pending').toString();
    let badgeClass = "badge bg-primary";
    const norm = statusStr.toLowerCase();
    if (norm.includes('completed') || norm.includes('approved') || norm === 'a') badgeClass = "badge bg-success";
    else if (norm.includes('rejected') || norm === 'r') badgeClass = "badge bg-danger";
    else if (norm.includes('pending') || norm.includes('process') || norm.includes('planning')) badgeClass = "badge bg-warning text-dark";
    
    return (
      <span className={badgeClass}>
        {statusStr}
      </span>
    );
  };

  const columns = [
    {
      title: "Employee ID",
      dataIndex: "EmployeeID",
      render: (text: any, record: any) => record.EmployeeID || record.EmpID || record.employeeId || '-',
      sorter: (a: any, b: any) => ((a.EmployeeID || a.EmpID || '') + '').localeCompare((b.EmployeeID || b.EmpID || '') + ''),
    },
    {
      title: "Employee Name",
      dataIndex: "EmployeeName",
      render: (text: any, record: any) => record.EmployeeName || record.AppraiseeName || record.employeeName || '-',
      sorter: (a: any, b: any) => ((a.EmployeeName || a.employeeName || '') + '').localeCompare((b.EmployeeName || b.employeeName || '') + ''),
    },
    {
      title: "Start Date",
      dataIndex: "StartDate",
      render: (text: any, record: any) => parseDotNetDate(record.StartDate || record.FromDate),
      sorter: (a: any, b: any) => ((a.StartDate || '') + '').localeCompare((b.StartDate || '') + ''),
    },
    {
      title: "End Date",
      dataIndex: "EndDate",
      render: (text: any, record: any) => parseDotNetDate(record.EndDate || record.ToDate),
      sorter: (a: any, b: any) => ((a.EndDate || '') + '').localeCompare((b.EndDate || '') + ''),
    },
    {
      title: "My Role",
      dataIndex: "Role",
      render: (text: any, record: any) => record.Role || record.myRole || record.role || '-',
      sorter: (a: any, b: any) => ((a.Role || '') + '').localeCompare((b.Role || '') + ''),
    },
    {
      title: "APAR Status",
      dataIndex: "APARStatus",
      render: (text: any, record: any) => statusBodyTemplate(record),
      sorter: (a: any, b: any) => ((a.APARStatus || '') + '').localeCompare((b.APARStatus || '') + ''),
    },
    {
      title: "APAR Substatus",
      dataIndex: "APARSubStatus",
      render: (text: any, record: any) => record.APARSubStatus || record.aparSubstatus || '-',
      sorter: (a: any, b: any) => ((a.APARSubStatus || '') + '').localeCompare((b.APARSubStatus || '') + ''),
    },
    {
      title: "Last Action Date",
      dataIndex: "LastActionDate",
      render: (text: any, record: any) => parseDotNetDate(record.LastActionDate),
      sorter: (a: any, b: any) => ((a.LastActionDate || '') + '').localeCompare((b.LastActionDate || '') + ''),
    },
    {
      title: "Template",
      dataIndex: "Template",
      render: (text: any, record: any) => record.Template || record.appraisalTemplateName || '-',
      sorter: (a: any, b: any) => ((a.Template || '') + '').localeCompare((b.Template || '') + ''),
    },
    {
      title: "Action",
      render: (text: any, record: any) => actionBodyTemplate(record),
    }
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">APAR Team Status Display</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.apar.dashboard}>Employee Appraisal</Link>
                  </li>
                  <li className="breadcrumb-item active">Team Status</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0 module-card mb-3">
            <div className="card-body p-3">
              <div className="d-flex flex-wrap align-items-end gap-3">
                <div className="flex-grow-1" style={{ maxWidth: '250px' }}>
                  <label className="form-label fs-13 text-muted mb-1">Appraisal Year</label>
                  <select 
                    className="form-select form-select-sm" 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="flex-grow-1" style={{ maxWidth: '250px' }}>
                  <label className="form-label fs-13 text-muted mb-1">Status Filter</label>
                  <select 
                    className="form-select form-select-sm" 
                    value={selectedStatus} 
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="pending">Pending / In Process</option>
                    <option value="approved">Approved / Completed</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <button className="btn btn-primary btn-sm px-4" onClick={fetchAppraisalStatus} disabled={isLoading}>
                    {isLoading ? <><i className="ti ti-loader fa-spin me-1"></i> Loading...</> : <><i className="ti ti-refresh me-1"></i> Refresh</>}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0 module-card">
            <div className="card-body p-0 overflow-hidden">
              <Datatable columns={columns} dataSource={filteredData} loading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default APARTeamStatus;
